/**
 * Add Employee Modal
 *
 * Modal form for adding or editing an employee in a submission.
 * Uses React Hook Form with Zod validation.
 */

import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  AddEmployeeRequest,
  EmployeeCalculation,
  PayIndicator,
  EmployeeCategory,
  PAY_INDICATOR_LABELS,
} from '@/types/nib/manualEntry';

// =============================================================================
// VALIDATION SCHEMA
// =============================================================================

// Define form data type explicitly to avoid zod inference issues with defaults
interface AddEmployeeFormData {
  ni_number: string;
  last_name: string;
  first_name: string;
  basic_wages: number;
  gratuity_wages: number;
  weeks_worked: number;
  pay_indicator: 'W' | 'B' | 'S' | 'M';
  category: number;
  save_as_profile: boolean;
}

const addEmployeeSchema = z.object({
  ni_number: z
    .string()
    .length(8, 'NI number must be exactly 8 digits')
    .regex(/^\d{8}$/, 'Must contain only digits'),
  last_name: z.string().min(1, 'Last name is required'),
  first_name: z.string().min(1, 'First name is required'),
  basic_wages: z
    .number({ invalid_type_error: 'Enter a valid amount' })
    .min(0, 'Wages cannot be negative'),
  gratuity_wages: z
    .number({ invalid_type_error: 'Enter a valid amount' })
    .min(0, 'Gratuity cannot be negative'),
  weeks_worked: z
    .number({ invalid_type_error: 'Enter weeks worked' })
    .min(1, 'Must be at least 1')
    .max(5, 'Cannot exceed 5'),
  pay_indicator: z.enum(['W', 'B', 'S', 'M']),
  category: z.number().min(1).max(7),
  save_as_profile: z.boolean(),
});

// =============================================================================
// CONSTANTS
// =============================================================================

const PAY_INDICATOR_OPTIONS: { value: PayIndicator; label: string }[] = [
  { value: 'W', label: 'Weekly' },
  { value: 'B', label: 'Bi-Weekly' },
  { value: 'S', label: 'Semi-Monthly' },
  { value: 'M', label: 'Monthly' },
];

const CATEGORY_OPTIONS: { value: EmployeeCategory; label: string }[] = [
  { value: 1, label: '1 - Regular (under 65)' },
  { value: 2, label: '2 - Retiree (under 65)' },
  { value: 3, label: '3 - Senior (65-69)' },
  { value: 4, label: '4 - Senior Retiree (65-69)' },
  { value: 5, label: '5 - Elderly (70+)' },
  { value: 6, label: '6 - Elderly Retiree (70+)' },
  { value: 7, label: '7 - Summer Student' },
];

// Max weeks based on pay indicator
const MAX_WEEKS_BY_INDICATOR: Record<PayIndicator, number> = {
  W: 5, // Weekly: up to 5 weeks
  B: 3, // Bi-Weekly: up to 3 periods (6 weeks / 2)
  S: 2, // Semi-Monthly: up to 2 periods
  M: 1, // Monthly: 1 period
};

// =============================================================================
// COMPONENT
// =============================================================================

interface AddEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AddEmployeeRequest) => Promise<void>;
  isLoading?: boolean;
  isHospitality: boolean;
  editingEmployee?: EmployeeCalculation | null;
}

export function AddEmployeeModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  isHospitality,
  editingEmployee,
}: AddEmployeeModalProps) {
  const isEditMode = !!editingEmployee;

  // Default values for the form
  const defaultValues: AddEmployeeFormData = useMemo(
    () => ({
      ni_number: '',
      last_name: '',
      first_name: '',
      basic_wages: 0,
      gratuity_wages: 0,
      weeks_worked: 1,
      pay_indicator: 'M' as PayIndicator,
      category: 1 as EmployeeCategory,
      save_as_profile: false,
    }),
    []
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AddEmployeeFormData>({
    resolver: zodResolver(addEmployeeSchema),
    defaultValues,
  });

  const payIndicator = watch('pay_indicator');
  const maxWeeks = MAX_WEEKS_BY_INDICATOR[payIndicator] || 5;

  // Reset form when modal opens/closes or editing employee changes
  useEffect(() => {
    if (isOpen) {
      if (editingEmployee) {
        reset({
          ni_number: editingEmployee.ni_number,
          last_name: editingEmployee.last_name,
          first_name: editingEmployee.first_name,
          basic_wages: editingEmployee.basic_wages,
          gratuity_wages: editingEmployee.gratuity_wages || 0,
          weeks_worked: editingEmployee.weeks_worked,
          pay_indicator: editingEmployee.pay_indicator,
          category: editingEmployee.category,
          save_as_profile: false,
        });
      } else {
        reset(defaultValues);
      }
    }
  }, [isOpen, editingEmployee, reset, defaultValues]);

  // Adjust weeks_worked if it exceeds max for selected pay indicator
  useEffect(() => {
    // React Hook Form's watch() returns new instances, cannot be memoized
    const currentWeeks = watch('weeks_worked');
    if (currentWeeks > maxWeeks) {
      setValue('weeks_worked', maxWeeks);
    }
  }, [payIndicator, maxWeeks, setValue, watch]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isLoading, onClose]);

  if (!isOpen) return null;

  const handleFormSubmit = async (data: AddEmployeeFormData) => {
    // Convert to API request format
    const request: AddEmployeeRequest = {
      ni_number: data.ni_number,
      last_name: data.last_name.toUpperCase(),
      first_name: data.first_name.toUpperCase(),
      basic_wages: data.basic_wages,
      gratuity_wages: isHospitality ? data.gratuity_wages : 0,
      weeks_worked: data.weeks_worked,
      pay_indicator: data.pay_indicator,
      category: data.category as EmployeeCategory,
      save_as_profile: data.save_as_profile,
    };

    await onSubmit(request);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={isLoading ? undefined : onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-employee-title"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2
                id="add-employee-title"
                className="text-xl font-semibold text-gray-900"
              >
                {isEditMode ? 'Edit Employee' : 'Add Employee'}
              </h2>
              <button
                onClick={onClose}
                disabled={isLoading}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                aria-label="Close modal"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="px-6 py-4 space-y-6 max-h-[60vh] overflow-y-auto">
              {/* Employee Info Section */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Employee Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* NI Number */}
                  <div>
                    <label
                      htmlFor="ni_number"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      NI Number *
                    </label>
                    <input
                      type="text"
                      id="ni_number"
                      maxLength={8}
                      placeholder="12345678"
                      disabled={isEditMode}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.ni_number
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300'
                      } ${isEditMode ? 'bg-gray-100' : ''}`}
                      {...register('ni_number')}
                    />
                    {errors.ni_number && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.ni_number.message}
                      </p>
                    )}
                  </div>

                  {/* First Name */}
                  <div>
                    <label
                      htmlFor="first_name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      First Name *
                    </label>
                    <input
                      type="text"
                      id="first_name"
                      placeholder="John"
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.first_name
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300'
                      }`}
                      {...register('first_name')}
                    />
                    {errors.first_name && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.first_name.message}
                      </p>
                    )}
                  </div>

                  {/* Last Name */}
                  <div>
                    <label
                      htmlFor="last_name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Last Name *
                    </label>
                    <input
                      type="text"
                      id="last_name"
                      placeholder="Smith"
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.last_name
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300'
                      }`}
                      {...register('last_name')}
                    />
                    {errors.last_name && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.last_name.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Wages Section */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Wages
                </h3>
                <div
                  className={`grid gap-4 ${isHospitality ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}
                >
                  {/* Basic Wages */}
                  <div>
                    <label
                      htmlFor="basic_wages"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Basic Wages *
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                        $
                      </span>
                      <input
                        type="number"
                        id="basic_wages"
                        step="0.01"
                        min="0"
                        placeholder="0.00"
                        className={`w-full pl-7 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          errors.basic_wages
                            ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                            : 'border-gray-300'
                        }`}
                        {...register('basic_wages', { valueAsNumber: true })}
                      />
                    </div>
                    {errors.basic_wages && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.basic_wages.message}
                      </p>
                    )}
                  </div>

                  {/* Gratuity Wages (Hospitality only) */}
                  {isHospitality && (
                    <div>
                      <label
                        htmlFor="gratuity_wages"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Gratuity/Tips
                      </label>
                      <div className="relative">
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                          $
                        </span>
                        <input
                          type="number"
                          id="gratuity_wages"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          className={`w-full pl-7 pr-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            errors.gratuity_wages
                              ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                              : 'border-gray-300'
                          }`}
                          {...register('gratuity_wages', {
                            valueAsNumber: true,
                          })}
                        />
                      </div>
                      {errors.gratuity_wages && (
                        <p className="mt-1 text-sm text-red-600">
                          {errors.gratuity_wages.message}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Work Period Section */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Work Period
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Pay Indicator */}
                  <div>
                    <label
                      htmlFor="pay_indicator"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Pay Frequency *
                    </label>
                    <select
                      id="pay_indicator"
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.pay_indicator
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300'
                      }`}
                      {...register('pay_indicator')}
                    >
                      {PAY_INDICATOR_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    {errors.pay_indicator && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.pay_indicator.message}
                      </p>
                    )}
                  </div>

                  {/* Weeks Worked */}
                  <div>
                    <label
                      htmlFor="weeks_worked"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Weeks/Periods Worked *
                    </label>
                    <select
                      id="weeks_worked"
                      className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        errors.weeks_worked
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300'
                      }`}
                      {...register('weeks_worked', { valueAsNumber: true })}
                    >
                      {Array.from({ length: maxWeeks }, (_, i) => i + 1).map(
                        num => (
                          <option key={num} value={num}>
                            {num}
                          </option>
                        )
                      )}
                    </select>
                    {errors.weeks_worked && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.weeks_worked.message}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Max {maxWeeks} for {PAY_INDICATOR_LABELS[payIndicator]}
                    </p>
                  </div>
                </div>
              </div>

              {/* Category Section */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">
                  Employee Category
                </h3>
                <div>
                  <label
                    htmlFor="category"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Category *
                  </label>
                  <select
                    id="category"
                    className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors.category
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : 'border-gray-300'
                    }`}
                    {...register('category', { valueAsNumber: true })}
                  >
                    {CATEGORY_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.category && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.category.message}
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Determines NIB contribution rates
                  </p>
                </div>
              </div>

              {/* Save as Profile Option */}
              {!isEditMode && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="save_as_profile"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    {...register('save_as_profile')}
                  />
                  <label
                    htmlFor="save_as_profile"
                    className="ml-2 text-sm text-gray-700"
                  >
                    Save as Employee Profile for future use
                  </label>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center gap-2"
              >
                {isLoading && (
                  <svg
                    className="animate-spin h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                {isLoading
                  ? 'Saving...'
                  : isEditMode
                    ? 'Update Employee'
                    : 'Add Employee'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddEmployeeModal;
